import { createSlice } from '@reduxjs/toolkit';
import {
    createPublishAsCompositionThunk,
    createUnpublishAsCompositionThunk,
    createLoadElearningItemsThunk,
    createLoadElearningItemThunk,
    createNewElearningItemThunk,
    deleteElearningItemThunk,
    saveElearningItemThunk,
} from '../elearning/elearning.js';
import { assignAndIsDirty } from '../../helpers/extend.js';

export const loadVimeoItemsIfNeeded = createLoadElearningItemsThunk('vimeo', 'vimeo');

export const loadVimeoItem = createLoadElearningItemThunk('vimeo');

export const publishVimeoItemAsComposition = createPublishAsCompositionThunk('vimeo');

export const unpublishVimeoItemAsComposition = createUnpublishAsCompositionThunk('vimeo');

export const createVimeoItem = createNewElearningItemThunk('vimeo', 'vimeo');

export const deleteVimeoItem = deleteElearningItemThunk('vimeo');

export const saveVimeoItem = saveElearningItemThunk('vimeo', 'vimeo');

const vimeoSlice = createSlice({
    name: 'vimeo',
    initialState: {
        status: 'idle',
        list: [],
        page: -1,
        error: null
    },
    reducers: {
        updateVimeoItem: (state, action) => {
            const { payload } = action;

            if (assignAndIsDirty(state.current, payload.changes)) {
                state.dirty = true;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadVimeoItemsIfNeeded.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(loadVimeoItemsIfNeeded.fulfilled, (state, action) => {
                const { payload } = action;

                state.status = 'success';
                state.list = payload.reload ? [...payload.list] : [...state.list, ...payload.list];
                state.page = payload.page;
                state.total = payload.total;
                state.search = payload.search;
            })
            .addCase(loadVimeoItemsIfNeeded.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            })
            .addCase(loadVimeoItem.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(loadVimeoItem.fulfilled, (state, action) => {
                const { payload } = action;
                const numVersions = payload.versions.length;

                state.status = 'success';
                state.current = {
                    ...payload,
                    lastVersion: numVersions > 0 ? payload.versions[numVersions - 1] : {}
                };
                state.dirty = false;
            })
            .addCase(loadVimeoItem.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            })
            .addCase(publishVimeoItemAsComposition.pending, (state) => {
                state.status = 'publishing';
            })
            .addCase(publishVimeoItemAsComposition.fulfilled, (state, action) => {
                const { payload } = action;
                let vimeoItem = state.list.find((i) => i.targetId === payload.targetId);

                state.status = 'success';
                vimeoItem = Object.assign(vimeoItem, payload);
            })
            .addCase(publishVimeoItemAsComposition.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            })
            .addCase(unpublishVimeoItemAsComposition.pending, (state) => {
                state.status = 'unpublishing';
            })
            .addCase(unpublishVimeoItemAsComposition.fulfilled, (state, action) => {
                const { payload } = action;
                let ltiItem = state.list.find((i) => i.targetId === payload.targetId);

                state.status = 'success';
                ltiItem = Object.assign(ltiItem, payload);
            })
            .addCase(unpublishVimeoItemAsComposition.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            })
            .addCase(createVimeoItem.pending, (state) => {
                state.status = 'creating';
            })
            .addCase(createVimeoItem.fulfilled, (state, action) => {
                const { payload } = action;

                state.status = 'success';
                state.list.unshift(payload);
                state.total++;
            })
            .addCase(createVimeoItem.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            })
            .addCase(deleteVimeoItem.pending, (state) => {
                state.status = 'deleting';
            })
            .addCase(deleteVimeoItem.fulfilled, (state, action) => {
                const { payload } = action;

                state.status = 'success';
                state.list = state.list.filter((i) => i.targetId !== payload.targetId);
                state.total--;
            })
            .addCase(deleteVimeoItem.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            })
            .addCase(saveVimeoItem.pending, (state) => {
                state.status = 'saving';
            })
            .addCase(saveVimeoItem.fulfilled, (state, action) => {
                const { payload } = action;
                const numVersions = payload.versions.length;

                state.status = 'success';
                state.current = {
                    ...payload,
                    lastVersion: numVersions > 0 ? payload.versions[numVersions - 1] : {}
                };
                state.dirty = false;
            })
            .addCase(saveVimeoItem.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error;
            });
    },
});

export const { updateVimeoItem } = vimeoSlice.actions;

export default vimeoSlice.reducer;
